/**
 * CONSTANTES DEL JUEGO - MALA HIERBA
 * Valores según Brief Técnico v1.0
 */

// ============================================
// CONFIGURACIÓN DE JUEGO
// ============================================

export const GAME_CONFIG = {
  WIDTH: 800,
  HEIGHT: 600,
  MOBILE_WIDTH: 375,
  MOBILE_MAX_WIDTH: 428,
  TABLET_WIDTH: 768,
  DESKTOP_WIDTH: 1280,
  TARGET_FPS: 60,
};

// ============================================
// VALORES INICIALES DE RONDA
// ============================================

export const ROUND_DEFAULTS = {
  INITIAL_VOTES: 20,
  INITIAL_TIME: 60, // segundos
  INITIAL_INTEGRITY: 100,
  WIN_THRESHOLD: 70, // % mínimo para ganar
  LOSE_THRESHOLD: 70, // % mínimo (debajo = derrota)
};

// ============================================
// DECAY (Deterioro Automático)
// ============================================

export const DECAY_CONFIG = {
  INITIAL_INTERVAL: 15, // segundos entre daños
  MIN_INTERVAL: 8, // mínimo en rondas avanzadas
  DECREASE_PER_ROUND: 2, // segundos que disminuye cada ronda
};

// ============================================
// COMPONENTES DE LA CASA
// ============================================

export const COMPONENTS = {
  // Componentes SIMPLES (1 tap repara todo)
  TECHO: {
    id: "techo",
    name: "TSE (Tribunal Supremo de Elecciones)",
    type: "simple",
    icon: "🏛️",
    color: "#4299e1",
    integrityValue: 15,
    critical: true,
    damageTypes: ["crack"],
    maxDamage: 5,
  },

  PAREDES: {
    id: "paredes",
    name: "Corte Suprema de Justicia",
    type: "multi",
    icon: "⚖️",
    color: "#718096",
    critical: false,
    subAreas: [
      {
        id: "izquierda",
        name: "Corte Izquierda",
        icon: "⚖️",
        integrityValue: 5,
      },
      { id: "derecha", name: "Corte Derecha", icon: "⚖️", integrityValue: 5 },
    ],
    maxDamage: 2,
  },

  FUNDACION: {
    id: "fundacion",
    name: "Constitución Política",
    type: "simple",
    icon: "📜",
    color: "#f6ad55",
    integrityValue: 20,
    critical: true,
    damageTypes: ["crack"],
    maxDamage: 5,
  },

  // Componentes CON SUB-ÁREAS (requieren múltiples taps)
  GARANTIAS: {
    id: "garantias",
    name: "Garantías Sociales",
    type: "multi",
    icon: "🏥",
    color: "#ed8936",
    critical: false,
    subAreas: [
      { id: "ccss", name: "CCSS", icon: "🏥", integrityValue: 10 },
      { id: "educacion", name: "Educación", icon: "📚", integrityValue: 9 },
      { id: "trabajo", name: "Trabajo", icon: "👷", integrityValue: 9 },
    ],
    maxDamage: 3,
  },

  MUNICIPALIDADES: {
    id: "municipalidades",
    name: "Municipalidades",
    type: "multi",
    icon: "🚪",
    color: "#b7791f",
    critical: false,
    subAreas: [
      { id: "ventana1", name: "Ventana 1", icon: "🪟", integrityValue: 8 },
      { id: "puerta", name: "Puerta", icon: "🚪", integrityValue: 9 },
      { id: "ventana2", name: "Ventana 2", icon: "🪟", integrityValue: 8 },
    ],
    maxDamage: 4,
  },
};

// ============================================
// TIPOS DE DAÑO
// ============================================

export const DAMAGE_TYPES = {
  CRACK: {
    id: "crack",
    name: "Grieta",
    symbol: "❌",
    description: "Daño estructural por neglect",
    color: "#dc2626",
  },
  VINE: {
    id: "vine",
    name: "Enredadera",
    symbol: "🌿",
    description: "Corrupción que crece",
    color: "#16a34a",
  },
};

// ============================================
// PROGRESIÓN DE RONDAS (Ciclos Electorales)
// ============================================

export const ROUNDS_PROGRESSION = [
  // Ronda 1: 2026-2030 - MÁS FÁCIL PARA TESTING
  {
    round: 1,
    cycle: "Siglo XVIX",
    votes: 15,
    time: 30,
    cracks: 4,
    vines: 5,
    decayInterval: 3,
    inheritedIntegrity: 90,
  },
  // Ronda 2: 2030-2034
  {
    round: 2,
    cycle: "1970",
    votes: 12,
    time: 30,
    cracks: 4,
    vines: 6,
    decayInterval: 3,
    inheritedIntegrity: 85,
  },
  // Ronda 3: 2034-2038
  {
    round: 3,
    cycle: "2026-2030",
    votes: 10,
    time: 25,
    cracks: 4,
    vines: 7,
    decayInterval: 7,
    inheritedIntegrity: 85,
  },
  // Ronda 4: 2038-2042
  {
    round: 4,
    cycle: "2038-2042",
    votes: 10,
    time: 20,
    cracks: 6,
    vines: 8,
    decayInterval: 8,
    inheritedIntegrity: 85,
  },
];

/**
 * MAPEO DE CASAS POR RONDA
 * Cada ronda tiene su propio set de casas (buena + mala)
 */
export const HOUSE_ASSETS = {
  1: {
    // Ronda 1 - Siglo XVIX (SIN CAMBIOS)
    good: "casa_adobe_buena",
    bad: "casa_adobe_mala",
    era: "Adobe Colonial",
  },
  2: {
    // Ronda 2 - 1970 (NUEVA: Caribbean)
    good: "casa_caribbean_buena",
    bad: "casa_caribbean_mala",
    era: "Caribbean",
  },
  3: {
    // Ronda 3 - 2026-2030 (CAMBIADA: Art Decó)
    good: "casa_artdeco_buena",
    bad: "casa_artdeco_mala",
    era: "Art Decó",
  },
  4: {
    // Ronda 4 - 2038-2042 (CAMBIADA: Modernismo)
    good: "casa_modernismo_buena",
    bad: "casa_modernismo_mala",
    era: "Modernismo",
  },
};

/**
 * ARRAYS DE ASSETS DE DAÑOS
 * Para selección aleatoria
 */
export const DAMAGE_ASSETS = {
  cracks: [
    "grieta_01",
    "grieta_02",
    "grieta_03",
    "grieta_04",
    "grieta_05",
    "grieta_06",
  ],
  vines: ["vine_01", "vine_02", "vine_03", "vine_04", "vine_05"],
};

/**
 * HELPER: Obtener grieta aleatoria
 */
export function getRandomCrack() {
  return DAMAGE_ASSETS.cracks[
    Math.floor(Math.random() * DAMAGE_ASSETS.cracks.length)
  ];
}

/**
 * HELPER: Obtener vine aleatoria
 */
export function getRandomVine() {
  return DAMAGE_ASSETS.vines[
    Math.floor(Math.random() * DAMAGE_ASSETS.vines.length)
  ];
}

/**
 * HELPER: Obtener casas de una ronda específica
 */
export function getHouseAssets(round) {
  return HOUSE_ASSETS[round] || HOUSE_ASSETS[1];
}

// ============================================
// COLORES DEL JUEGO
// ============================================

export const COLORS = {
  // Componentes
  TECHO: "#4299e1",
  GARANTIAS: "#ed8936",
  PAREDES: "#718096",
  MUNICIPALIDADES: "#b7791f",
  FUNDACION: "#f6ad55",

  // UI
  BACKGROUND: "#667eea",
  BACKGROUND_GRADIENT_END: "#764ba2",
  HUD_BG: "#ffffff",
  TEXT_PRIMARY: "#2d3748",
  TEXT_LIGHT: "#ffffff",

  // Estados de Integridad
  INTEGRITY_HIGH: "#48bb78", // > 70%
  INTEGRITY_MEDIUM: "#f6ad55", // 40-70%
  INTEGRITY_LOW: "#dc2626", // < 40%

  // Feedback
  SUCCESS: "#48bb78",
  DANGER: "#dc2626",
  WARNING: "#f59e0b",
};

// ============================================
// MENSAJES DEL JUEGO
// ============================================

export const MESSAGES = {
  DEFEAT: {
    VOTES_LEFT: "Tenías {votes} votos más. ¿Por qué no votaste?",
    TIME_OUT: "El tiempo se agotó. La democracia no espera.",
    CRITICAL_FAILED: "La {component} colapsó. Sin ella, no hay democracia.",
    LOW_INTEGRITY:
      "La integridad cayó a {integrity}%. La casa no puede sostenerse.",
  },

  VICTORY: {
    CYCLE_COMPLETE: "¡Votando, la democracia gana!",
    NEXT_CYCLE: "Pasas al siguiente ciclo: {nextCycle}",
    INTEGRITY_MAINTAINED: "Mantuviste la integridad en {integrity}%",
  },

  REPAIR: {
    COMPONENT_FIXED: "¡{component} reforzado!",
    CRACK_REPAIRED: "Grieta reparada",
    VINE_REMOVED: "Enredadera eliminada",
    INTEGRITY_GAINED: "+{value}% Integridad",
  },
};

// ============================================
// AUDIO (Rutas a assets)
// ============================================

export const AUDIO = {
  SFX: {
    CLEAN: "clean",
    CUT: "cut",
    WIN: "win",
    LOSE: "lose",

    // TAP: "tap",
    // CRACK_APPEAR: "crack",
    // REPAIR: "repair",
    // VINE_GROW: "vine-grow",
    // VINE_CUT: "vine-cut",
    // VICTORY: "victory",
    // DEFEAT: "defeat",
    // HOUSE_SHAKE: "shake",
    // POWER_UP: "powerup",
    // WARNING: "warning",
    // UI_BEEP: "beep",
  },
  MUSIC: {
    GAME_THEME: "game_theme",
    FINAL_VICTORY: "final_victory",
  },
};

// ============================================
// CONFIGURACIÓN DE PHASER SCENES
// ============================================

export const SCENES = {
  BOOT: "BootScene",
  MENU: "MenuScene",
  GAME: "GameScene",
  VICTORY: "VictoryScene",
  DEFEAT: "DefeatScene",
  FINAL_VICTORY: "FinalVictoryScene",
};

// ============================================
// STORAGE KEYS (localStorage)
// ============================================

export const STORAGE_KEYS = {
  HIGH_SCORE: "mala-hierba-high-score",
  CURRENT_ROUND: "mala-hierba-current-round",
  CURRENT_INTEGRITY: "mala-hierba-integrity",
  TOTAL_CYCLES: "mala-hierba-total-cycles",
};

// ============================================
// TAGLINE Y TEXTOS
// ============================================

export const TEXTS = {
  TAGLINE: "La democracia se cuida votando.\n    Saca la Mala Hierba de raíz",
  GAME_TITLE: "MALA HIERBA",
  PLAY_BUTTON: "JUGAR",
  HOW_TO_PLAY: "¿Cómo Jugar?",
  CONTINUE: "CONTINUAR",
  RETRY: "REINTENTAR",
  SHARE: "COMPARTIR",
  CLAUSURADO: "CLAUSURADO",
};

// ============================================
// CONFIGURACIÓN DE BOTONES DE CONTROL
// ============================================

/**
 * Configuración visual y posición de botones
 */
export const CONTROL_BUTTONS = {
  MUTE: {
    x: -90, // Relativo al ancho (width - 90)
    y: 20,
    width: 40,
    height: 40,
    iconMuted: "🔇",
    iconUnmuted: "🔊",
    color: 0x4a5568,
    hoverColor: 0x2d3748,
  },
  PAUSE: {
    x: -50, // Relativo al ancho (width - 50)
    y: 20,
    width: 40,
    height: 40,
    icon: "⏸",
    color: 0x4a5568,
    hoverColor: 0x2d3748,
  },
  QUIT: {
    x: -10, // Relativo al ancho (width - 10)
    y: 20,
    width: 40,
    height: 40,
    icon: "✕",
    color: 0xdc2626,
    hoverColor: 0xb91c1c,
  },
};

/**
 * Textos del overlay de pausa
 */
export const PAUSE_TEXTS = {
  TITLE: "JUEGO PAUSADO",
  CONTINUE: "Continuar",
  QUIT: "Salir al Menú",
};

/**
 * Textos del modal de confirmación de quit
 */
export const QUIT_TEXTS = {
  TITLE: "¿Salir del juego?",
  MESSAGE: "Perderás todo el progreso de esta ronda",
  CONFIRM: "Sí, Salir",
  CANCEL: "Cancelar",
};

export default {
  GAME_CONFIG,
  ROUND_DEFAULTS,
  DECAY_CONFIG,
  COMPONENTS,
  DAMAGE_TYPES,
  ROUNDS_PROGRESSION,
  COLORS,
  MESSAGES,
  AUDIO,
  SCENES,
  STORAGE_KEYS,
  TEXTS,
  CONTROL_BUTTONS,
  PAUSE_TEXTS,
  QUIT_TEXTS,
};
